/* eslint-disable no-unused-vars */
/**
 * @file Provides a single entrypoint for VueJS components.
 *
 * This file will not be used unless pages render the element mentioned in the $mount() call below.
 * Currently, that is <div id="raft-js-app" />. See B4Integration.class.php and various
 * .module and .inc files in bioraft3, and various controllers in bioraft4, all of which currently
 * use <div id="raft-js" />
 */

// Due to ES6 import use, need to import logic for webpack publicPath first
import "./entry.js";
import Vue from "vue";
// eslint-disable-next-line import/no-extraneous-dependencies
import VCalendar from "v-calendar";
import PrettyCheckbox from "pretty-checkbox-vue";
import VModal from "vue-js-modal";
import VueProgressBar from "vue-progressbar";
import "@/composables/useNotification";
import VueRouter from "vue-router";
import BootstrapVue from "bootstrap-vue";
import {
  chemicalContainersRoutes,
  chemicalContainersGlobalComponents,
} from "@/pages/chemtracker/ChemicalContainers";
import {
  chemicalInventoryReconciliationRoutes,
  chemicalInventoryReconciliationGlobalComponents,
} from "@/pages/chemtracker/ChemicalInventoryReconciliation";
import {
  chemicalReconciliationHistoryRoutes,
  chemicalReconciliationHistoryGlobalComponents,
} from "@/pages/chemtracker/ChemicalReconciliationHistory";
import {
  orderManagementRoutes,
  orderManagementGlobalComponents,
} from "@/pages/chemtracker/OrderManagement";
import {
  chemdbTypeRoutes,
  chemdbTypeGlobalComponents,
} from "@/pages/chemdb/chemdb_type.js";
import {
  incidentManagementGlobalComponents,
  incidentManagementRoutes,
} from "@/pages/incident_management/IncidentManagement";
import {
  medSurveillanceRoutes,
  medSurveillanceGlobalComponents,
} from "@/pages/medical_surveillance/MedSurveillance.js";
import { scisureRoutes } from "@/pages/platform/ScisureIntegration.js";
import {
  requirementRuleResetRoutes,
  requirementRuleResetGlobalComponents,
} from "@/pages/requirements/RequirementRuleReset.js";
import VHomepageBlocks from "./components/raft/VHomepageBlocks.vue";
import ChemicalStructureSearch from "./pages/ChemicalStructureSearchRoutes";
import UserJobRoutes from "./pages/UserJobRoutes";
import ChemtrackerSdsRoutes from "./pages/chemtracker/ChemtrackerSdsRoutes";
import store from "./store";
import App from "./components/App.vue";
import HazardousWaste from "./pages/hazardous_waste/HazardousWaste";
import Requirements from "./pages/requirements/Requirements";
import {
  safetyObservationRoutes,
  safetyObservationGlobalComponents,
} from "./pages/safety_observation/SafetyObservation.js";
import { messagingRoutes } from "./pages/raft_messaging/RaftMessagingRoutes.js";
import {
  metadataEntityRoutes,
  metadataEntityGlobalComponents,
} from "./pages/raft_metadata_entities/MetadataEntity.js";
import {
  biologicalsRoutes,
  biologicalsGlobalComponents,
} from "./pages/biologicals/Biologicals";
import FireCodeRoutes from "./pages/FireCodeRoutes.js";
import {
  bioSurveyRoutes,
  bioSurveyGlobalComponents,
} from "./pages/biologicals/BioSurveyRoutes.js";
import { bioB4ListingRoutes } from "./pages/biologicals/BioB4Listings.js";
import { bioListingGlobalComponents } from "./pages/biologicals/BioListing.js";
import {
  bioMaterialsRoutes,
  bioMaterialsGlobalComponents,
} from "./pages/biologicals/BioMaterial.js";
import {
  spacesListingRoutes,
  spacesListingGlobalComponents,
} from "./pages/platform/SpacesListing.js";
import {
  trainingRoutes,
  trainingGlobalComponents,
} from "./pages/training/training_course.js";
import {
  documentRoutes,
  documentGlobalComponents,
} from "./pages/raft_documents/Document.js";

// This looks like these imports are not being used, but it is critical for
// making a vuejs component available as a custom web component.
import BioSpaceActivitiesWebComponent from "./pages/biologicals/BioSpaceActivitiesWebComponent";
import BioSpaceSpecifyUsageParentWebComponent from "./pages/biologicals/SpaceMaterialsUsageWebComponent";

import { useDrupalSettings } from "./composables/useDrupalSettings";
import { useCloudWatchRum } from "./composables/useCloudWatchRum";

const { initializeRum } = useCloudWatchRum();

initializeRum();

const VNotificationsAppWrapped = () =>
  import("./components/raft/VNotificationsAppWrapped.vue");
const VTest = () => import("./components/VTest.vue");

Vue.use(BootstrapVue);
Vue.use(VCalendar);
const options = {
  color: "#41b883",
  failedColor: "#874b4b",
  thickness: "8px",
  transition: {
    speed: "0.02s",
    opacity: "0.8s",
    termination: 9000,
  },
  autoRevert: true,
  autoFinish: false,
  location: "top",
  inverse: false,
};
Vue.use(VueProgressBar, options);
Vue.use(VModal, { dialog: true });
Vue.use(PrettyCheckbox);

const { config } = useDrupalSettings();
Vue.prototype.$isB4 = config.isB4;

const notifs = new Vue({
  name: "NotificationsApp",
  provide() {
    return {
      isB4: this.$isB4,
    };
  },
  render(h) {
    return h(VNotificationsAppWrapped);
  },
});

let loggedIn = false;
try {
  loggedIn = Drupal?.settings?.user?.current?.[0] || drupalSettings?.user?.uid;
} catch (e) {}
if (loggedIn) {
  notifs.$mount("#mount-notifications");
}

const routes = [
  ...ChemicalStructureSearch,
  ...UserJobRoutes,
  ...ChemtrackerSdsRoutes,
  ...HazardousWaste,
  ...Requirements,
  ...safetyObservationRoutes,
  ...biologicalsRoutes,
  ...messagingRoutes,
  ...FireCodeRoutes,
  ...chemicalContainersRoutes,
  ...chemicalInventoryReconciliationRoutes,
  ...chemicalReconciliationHistoryRoutes,
  ...chemdbTypeRoutes,
  ...orderManagementRoutes,
  ...metadataEntityRoutes,
  ...bioSurveyRoutes,
  ...medSurveillanceRoutes,
  ...incidentManagementRoutes,
  ...bioB4ListingRoutes,
  ...bioMaterialsRoutes,
  ...scisureRoutes,
  ...spacesListingRoutes,
  ...requirementRuleResetRoutes,
  ...trainingRoutes,
  ...documentRoutes,
  {
    name: "homepage",
    path: "/",
    alias: ["/home", "/frontpage_panel"],
    component: VHomepageBlocks,
  },
];
Vue.use(VueRouter);
const router = new VueRouter({
  mode: "history",
  routes,
});

router.beforeResolve((to, from, next) => {
  next();
});

Vue.component("App", App);
Vue.component("VTest", VTest);

const globalComponents = {
  ...biologicalsGlobalComponents,
  ...chemicalContainersGlobalComponents,
  ...chemicalInventoryReconciliationGlobalComponents,
  ...chemicalReconciliationHistoryGlobalComponents,
  ...orderManagementGlobalComponents,
  ...chemdbTypeGlobalComponents,
  ...metadataEntityGlobalComponents,
  ...incidentManagementGlobalComponents,
  ...bioListingGlobalComponents,
  ...bioMaterialsGlobalComponents,
  ...spacesListingGlobalComponents,
  ...requirementRuleResetGlobalComponents,
  ...bioSurveyGlobalComponents,
};

Object.keys(globalComponents).forEach((key) => {
  Vue.component(key, globalComponents[key]);
});

// Enable performance stats to be viewed.
Vue.config.performance = true;

const vm = new Vue({
  name: "SingleBundle",
  router,
  store,
  components: {
    ...biologicalsGlobalComponents,
    ...metadataEntityGlobalComponents,
    ...medSurveillanceGlobalComponents,
    ...incidentManagementGlobalComponents,
    ...bioListingGlobalComponents,
    ...bioMaterialsGlobalComponents,
    ...trainingGlobalComponents,
    ...documentGlobalComponents,
    ...safetyObservationGlobalComponents,
  },
  provide() {
    return {
      isB4: this.$isB4,
    };
  },
});

const matched = router.getMatchedComponents(window.location.pathname);

const singleBundleExpected = matched.length > 0;
const newTargetExists = document.getElementById("raft-js-app");
const oldTarget = document.getElementById("raft-js") && matched.length > 0;

if (newTargetExists) {
  vm.$mount("#raft-js-app");
} else if (singleBundleExpected && oldTarget) {
  // eslint-disable-next-line no-console
  console.warn("Page should be updated to include #raft-js-app.");
  vm.$mount("#raft-js");
}
